/* iter-p2-b — SEO Config admin panel.
 *
 * Small form for social profile URLs + og:image management. The
 * "Generate og:image" button fires the admin nano-banana endpoint
 * which produces a 1200x630 branded card and pins the resulting URL
 * on the SEO config in one shot.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Globe, Save, Loader2, Sparkles, ImageIcon, ExternalLink, Pin, X,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const PROFILE_ROWS = [
  { key: 'twitter',   label: 'Twitter / X',  placeholder: 'https://twitter.com/crownandrose' },
  { key: 'instagram', label: 'Instagram',    placeholder: 'https://instagram.com/crownandrose' },
  { key: 'linkedin',  label: 'LinkedIn',     placeholder: 'https://linkedin.com/company/crownandrose' },
  { key: 'youtube',   label: 'YouTube',      placeholder: 'https://youtube.com/@crownandrose' },
];

function looksValid(u) {
  if (!u) return true;
  try { const p = new URL(u); return p.protocol === 'http:' || p.protocol === 'https:'; }
  catch { return false; }
}

const GENRE_OPTIONS = [
  { value: '',              label: 'Default (library / oxblood + gold)' },
  { value: 'dark-romance',  label: 'Dark Romance' },
  { value: 'literary',      label: 'Literary Fiction' },
  { value: 'thriller',      label: 'Thriller' },
  { value: 'sci-fi',        label: 'Sci-Fi' },
  { value: 'fantasy',       label: 'Fantasy' },
];

export default function SeoConfigAdminPanel() {
  const [profiles, setProfiles] = useState({ twitter: '', instagram: '', linkedin: '', youtube: '' });
  const [ogUrl, setOgUrl]       = useState('');
  const [ogPrompt, setOgPrompt] = useState('');
  const [ogGenre, setOgGenre]   = useState('');
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [generating, setGen]    = useState(false);
  // iter-p2-d — Bulk-preview: after a count=3 run, admin sees three
  // thumbnails and pins one (or discards the batch).
  const [candidates, setCandidates] = useState([]);
  const [pinning, setPinning]       = useState('');    // url currently being pinned

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/seo-config`);
      const incoming = r.data?.social_profiles || [];
      // Best-effort match incoming URLs into our four labelled slots.
      const next = { twitter: '', instagram: '', linkedin: '', youtube: '' };
      for (const u of incoming) {
        const lower = u.toLowerCase();
        if      (/twitter\.com|x\.com/.test(lower))  next.twitter   = u;
        else if (/instagram\.com/.test(lower))       next.instagram = u;
        else if (/linkedin\.com/.test(lower))        next.linkedin  = u;
        else if (/youtube\.com|youtu\.be/.test(lower)) next.youtube = u;
      }
      setProfiles(next);
      setOgUrl(r.data?.og_image_url || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load SEO config.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    for (const row of PROFILE_ROWS) {
      if (!looksValid(profiles[row.key])) {
        toast.error(`Invalid URL for ${row.label}.`);
        return;
      }
    }
    if (!looksValid(ogUrl)) {
      toast.error('og:image URL must be http(s)://');
      return;
    }
    setSaving(true);
    try {
      const social_profiles = PROFILE_ROWS
        .map((r) => (profiles[r.key] || '').trim())
        .filter((u) => u);
      const r = await axios.put(`${API}/admin/seo-config`, {
        social_profiles,
        og_image_url: (ogUrl || '').trim(),
      });
      setOgUrl(r.data?.og_image_url || '');
      toast.success('SEO config saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const generate = async (count = 1) => {
    if (generating) return;
    setGen(true);
    setCandidates([]);
    try {
      const r = await axios.post(`${API}/admin/seo-config/generate-og-image`, {
        prompt: ogPrompt.trim() || undefined,
        genre:  ogGenre || undefined,
        count,
      }, { timeout: 240000 });
      const cands = r.data?.candidates || (r.data?.og_image_url ? [r.data.og_image_url] : []);
      if (count === 1) {
        // Server auto-pins the single-candidate result.
        setOgUrl(r.data?.og_image_url || '');
        toast.success('og:image generated and pinned.');
      } else {
        // Bulk run — show the picker, don't auto-pin.
        setCandidates(cands);
        if (cands.length < count) {
          toast.warning(`Only ${cands.length} of ${count} candidates rendered. Pick or regenerate.`);
        } else {
          toast.success(`${cands.length} candidates ready — pin the one you like.`);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Nano Banana generation failed. Try again in a moment.');
    } finally { setGen(false); }
  };

  const pinCandidate = async (u) => {
    if (pinning) return;
    setPinning(u);
    try {
      const r = await axios.post(`${API}/admin/seo-config/pin-og-image`, {
        og_image_url: u,
      });
      setOgUrl(r.data?.og_image_url || u);
      setCandidates([]);
      toast.success('Pinned as the active og:image.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pin failed.');
    } finally { setPinning(''); }
  };

  const discardCandidate = (u) => {
    setCandidates((prev) => prev.filter((c) => c !== u));
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4" data-testid="admin-seo-config-panel">
      <div className="flex items-start gap-2">
        <Globe className="mt-0.5 h-5 w-5 text-primary" />
        <div>
          <h2 className="text-[18px] font-semibold"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            SEO Config · social profiles + og:image
          </h2>
          <p className="text-[11.5px] text-muted-foreground">
            Social profile URLs flow into schema.org <code>sameAs</code> on the
            landing organization card; the og:image URL flows into
            <code> &lt;meta property="og:image"&gt;</code> for every social share.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Social profiles */}
          <div className="rounded-md border border-border bg-card p-3">
            <div className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
              <Globe className="h-3 w-3 text-primary" />
              Social profiles
            </div>
            <div className="space-y-2">
              {PROFILE_ROWS.map((r) => {
                const bad = !looksValid(profiles[r.key]);
                return (
                  <div key={r.key}
                       className="grid grid-cols-[100px_1fr] items-center gap-2"
                       data-testid={`admin-seo-profile-row-${r.key}`}>
                    <Label className="text-[11.5px]">{r.label}</Label>
                    <Input
                      value={profiles[r.key] || ''}
                      onChange={(e) => setProfiles((p) => ({ ...p, [r.key]: e.target.value }))}
                      placeholder={r.placeholder}
                      className={bad ? 'border-destructive' : ''}
                      data-testid={`admin-seo-profile-input-${r.key}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* OG Image */}
          <div className="rounded-md border border-border bg-card p-3">
            <div className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
              <ImageIcon className="h-3 w-3 text-primary" />
              og:image · social share card (1200 × 630)
            </div>
            {ogUrl ? (
              <div className="mb-2 flex items-center gap-2">
                <img src={ogUrl} alt="Current og:image"
                     className="h-24 rounded-sm border border-border object-cover"
                     data-testid="admin-seo-og-preview" />
                <a href={ogUrl} target="_blank" rel="noopener noreferrer"
                   className="text-[11px] text-primary hover:underline">
                  <ExternalLink className="mr-0.5 inline h-3 w-3" />
                  Open
                </a>
              </div>
            ) : (
              <p className="mb-2 text-[11.5px] text-muted-foreground"
                 data-testid="admin-seo-og-empty">
                No og:image set. Generate one with Nano Banana below, or paste a URL.
              </p>
            )}

            <Label className="text-[11.5px] text-muted-foreground">og:image URL</Label>
            <Input
              value={ogUrl}
              onChange={(e) => setOgUrl(e.target.value)}
              placeholder="https://.../og.png"
              data-testid="admin-seo-og-url"
              className={!looksValid(ogUrl) ? 'border-destructive' : ''}
            />

            <div className="mt-3 rounded-sm border border-dashed border-primary/40 bg-primary/5 p-2">
              <Label className="text-[11px] uppercase tracking-wider text-primary">
                Generate with Nano Banana
              </Label>
              <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_180px]">
                <Textarea
                  value={ogPrompt}
                  onChange={(e) => setOgPrompt(e.target.value)}
                  rows={2}
                  placeholder="Optional — override the default oxblood-and-gold library prompt."
                  data-testid="admin-seo-og-prompt"
                />
                <div className="flex flex-col gap-1">
                  <Label className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                    Genre preset
                  </Label>
                  <select
                    value={ogGenre}
                    onChange={(e) => setOgGenre(e.target.value)}
                    className="h-9 rounded-sm border border-border bg-background px-2 text-[12px]"
                    data-testid="admin-seo-og-genre"
                  >
                    {GENRE_OPTIONS.map((g) => (
                      <option key={g.value} value={g.value}>{g.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-2">
                <Button
                  onClick={() => generate(1)}
                  disabled={generating || pinning}
                  className="gap-1"
                  data-testid="admin-seo-og-generate"
                >
                  {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {generating ? 'Generating (10-30s)…' : 'Generate og:image'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => generate(3)}
                  disabled={generating || pinning}
                  className="gap-1"
                  data-testid="admin-seo-og-generate-3"
                  title="Render three candidates in parallel and pick the winner."
                >
                  {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  Generate 3 candidates
                </Button>
              </div>
            </div>

            {/* iter-p2-d — Bulk candidates picker. Only rendered after a
                count=3 run. Pin one URL (server-validates the path) or
                discard the batch entirely. */}
            {candidates.length > 0 && (
              <div className="mt-3 rounded-sm border border-primary/40 bg-primary/[.03] p-2"
                   data-testid="admin-seo-og-candidates">
                <div className="mb-1.5 flex items-center justify-between">
                  <Label className="text-[11px] uppercase tracking-wider text-primary">
                    {candidates.length} candidate{candidates.length === 1 ? '' : 's'} · pick one
                  </Label>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 gap-1 text-[10.5px] text-muted-foreground hover:text-rose-600"
                    onClick={() => setCandidates([])}
                    data-testid="admin-seo-og-candidates-clear"
                  >
                    <X className="h-3 w-3" /> Discard all
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {candidates.map((u, i) => {
                    const isBusy = pinning === u;
                    return (
                      <div key={u}
                           className="overflow-hidden rounded-sm border border-border bg-background"
                           data-testid={`admin-seo-og-candidate-${i}`}>
                        <img src={u} alt={`Candidate ${i + 1}`}
                             className="h-24 w-full object-cover"
                             data-testid={`admin-seo-og-candidate-thumb-${i}`} />
                        <div className="flex items-center justify-between gap-1 p-1.5">
                          <a href={u} target="_blank" rel="noopener noreferrer"
                             className="text-[10.5px] text-muted-foreground hover:text-primary"
                             data-testid={`admin-seo-og-candidate-open-${i}`}>
                            <ExternalLink className="mr-0.5 inline h-3 w-3" />
                            Open
                          </a>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 gap-0.5 px-1.5 text-[10.5px] text-muted-foreground hover:text-rose-600"
                              onClick={() => discardCandidate(u)}
                              disabled={isBusy || !!pinning}
                              data-testid={`admin-seo-og-candidate-discard-${i}`}
                            >
                              <X className="h-3 w-3" /> Discard
                            </Button>
                            <Button
                              size="sm"
                              className="h-6 gap-0.5 px-1.5 text-[10.5px]"
                              onClick={() => pinCandidate(u)}
                              disabled={isBusy || !!pinning}
                              data-testid={`admin-seo-og-candidate-pin-${i}`}
                            >
                              {isBusy
                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                : <Pin className="h-3 w-3" />}
                              Pin
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="gap-1"
                    data-testid="admin-seo-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save SEO config
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
